import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UpdateUserInput } from '@proref/schemas';
import type { AdminUserListItem, UserRole } from '@proref/types';
import { Modal } from '../shared/Modal';
import { Field } from '../shared/Field';
import { Input } from '../shared/Input';
import { Select } from '../shared/Select';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Icon } from '../shared/Icons';
import { useUpdateUser } from '../../hooks/use-users';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useSportsQuery } from '../../hooks/use-sports';
import { useAuth } from '../../contexts/auth-context';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';

interface FormValues {
  full_name: string;
  // '' means "no licence" — the API normalises the empty string back to NULL.
  licence_level: string;
  role: Exclude<UserRole, 'super_admin'>;
  federation_id: string;
  sport_id: string;
}

function toFormValues(user: AdminUserListItem): FormValues {
  return {
    full_name: user.full_name,
    licence_level: user.licence_level ?? '',
    // A super_admin target has no tenant-scoped role; the control is disabled for them anyway,
    // so 'referee' here is only a placeholder the diff below can never pick up.
    role: user.role === 'super_admin' ? 'referee' : user.role,
    federation_id: user.federation_id ?? '',
    sport_id: user.sport_id ?? '',
  };
}

// Edit one roster member, from either admin roster. The editable surface narrows with who's
// editing whom, matching the server's guards exactly so the form can never compose a body the
// API will refuse:
//   • a federation_admin sees name + licence + role (their federation is the only one there is
//     for them, and sending a tenancy field would be a 403);
//   • a super_admin also gets federation + sport, i.e. re-homing a mis-assigned account;
//   • nobody edits their own role or tenancy, and a ProRef admin's role/tenancy is fixed —
//     both are shown disabled with the reason, rather than hidden and silently rejected.
export function EditUserDialog({ user }: { user: AdminUserListItem }) {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const update = useUpdateUser();
  const [open, setOpen] = useState(false);

  const isSuperAdminActor = me?.role === 'super_admin';
  const isSelf = me?.id === user.id;
  const targetIsOperator = user.role === 'super_admin';
  // Role and tenancy move together: every case that locks one locks the other.
  const roleLocked = isSelf || targetIsOperator;
  const canMoveTenant = isSuperAdminActor && !roleLocked;

  // Only fetched when the tenant selects are actually rendered — a federation_admin has no
  // access to /v1/federations, so an unconditional query would 403 on every open.
  const { data: federations } = useFederationsQuery(canMoveTenant);
  const { data: sports } = useSportsQuery(canMoveTenant);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: toFormValues(user) });

  // The row can be refetched underneath an open dialog (the roster revalidates on a window
  // focus), so re-seed the form whenever the user prop changes identity or the dialog reopens.
  useEffect(() => {
    if (open) reset(toFormValues(user));
  }, [open, user, reset]);

  const onSubmit = handleSubmit(async (values) => {
    // Send only what actually moved. The API refuses a federation_admin that sends any tenancy
    // key, and refuses a role/tenancy key on a ProRef admin — echoing unchanged values back
    // would turn a plain rename into a 403.
    const patch: UpdateUserInput = {};
    if (values.full_name !== user.full_name) patch.full_name = values.full_name;
    if (values.licence_level !== (user.licence_level ?? '')) {
      patch.licence_level = values.licence_level === '' ? null : values.licence_level;
    }
    if (!roleLocked && values.role !== user.role) patch.role = values.role;
    if (canMoveTenant) {
      if (values.federation_id !== (user.federation_id ?? '')) patch.federation_id = values.federation_id;
      if (values.sport_id !== (user.sport_id ?? '')) patch.sport_id = values.sport_id;
    }

    if (Object.keys(patch).length === 0) {
      notifyWarning(t('admin.users.editDialog.nothingChangedToast'));
      setOpen(false);
      return;
    }

    try {
      const updated = await update.mutateAsync({ id: user.id, input: patch });
      notifySuccess(t('admin.users.editDialog.updatedToast', { name: updated.full_name }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.users.editDialog.errorToast'));
    }
  });

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon
        aria-label={t('admin.users.editDialog.editAria', { name: user.full_name })}
        onClick={() => setOpen(true)}
      >
        <Icon.pencil className="h-3.5 w-3.5" />
      </Button>

      <Modal
        open={open}
        onClose={update.isPending ? () => {} : () => setOpen(false)}
        title={t('admin.users.editDialog.title', { name: user.full_name })}
        sub={user.email}
        footer={
          <>
            <Button onClick={() => setOpen(false)} disabled={update.isPending}>
              {t('common.cancel')}
            </Button>
            <Button form="edit-user-form" type="submit" variant="accent" disabled={update.isPending}>
              {update.isPending ? (
                <>
                  <Spinner className="w-3.5 h-3.5" /> {t('admin.users.editDialog.saving')}
                </>
              ) : (
                t('admin.users.editDialog.saveChanges')
              )}
            </Button>
          </>
        }
      >
        <form id="edit-user-form" onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
          <Field label={t('admin.users.editDialog.fullNameLabel')} htmlFor="edit-user-name" error={errors.full_name?.message}>
            <Input
              id="edit-user-name"
              {...register('full_name', {
                required: t('admin.users.editDialog.fullNameRequired'),
                maxLength: { value: 100, message: t('admin.users.editDialog.fullNameMaxLength') },
              })}
            />
          </Field>

          <Field label={t('admin.users.editDialog.licenceLabel')} htmlFor="edit-user-licence" error={errors.licence_level?.message}>
            <Input
              id="edit-user-licence"
              placeholder={t('admin.users.editDialog.licencePlaceholder')}
              {...register('licence_level', {
                maxLength: { value: 60, message: t('admin.users.editDialog.licenceMaxLength') },
              })}
            />
            <p className="mt-1 text-[11px] text-muted">{t('admin.users.editDialog.licenceHint')}</p>
          </Field>

          <Field label={t('admin.users.editDialog.roleLabel')} htmlFor="edit-user-role">
            <Select id="edit-user-role" disabled={roleLocked} {...register('role')}>
              <option value="referee">{t('admin.users.editDialog.roleReferee')}</option>
              <option value="federation_admin">{t('admin.users.editDialog.roleFederationAdmin')}</option>
            </Select>
            {roleLocked && (
              <p className="mt-1 text-[11px] text-muted">
                {targetIsOperator
                  ? t('admin.users.editDialog.roleLockedOperator')
                  : t('admin.users.editDialog.roleLockedSelf')}
              </p>
            )}
          </Field>

          {canMoveTenant && (
            <>
              <Field label={t('admin.users.editDialog.federationLabel')} htmlFor="edit-user-federation">
                <Select id="edit-user-federation" {...register('federation_id')}>
                  {(federations ?? []).map((fed) => (
                    <option key={fed.id} value={fed.id}>
                      {fed.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label={t('admin.users.editDialog.sportLabel')} htmlFor="edit-user-sport">
                <Select id="edit-user-sport" {...register('sport_id')}>
                  {(sports ?? []).map((sport) => (
                    <option key={sport.id} value={sport.id}>
                      {sport.name}
                    </option>
                  ))}
                </Select>
                {/* Re-homing is a correction tool, not a merge — say what carries over. */}
                <p className="mt-1 text-[11px] text-muted">
                  {t('admin.users.editDialog.sportHint')}
                </p>
              </Field>
            </>
          )}
        </form>
      </Modal>
    </>
  );
}
